/**
 * The one rule for "which stored values does a Picker field paint as NOTHING SELECTED?", shared by the
 * edit broker, its cascade subclass, and the Read/Print display provider so they cannot drift.
 *
 * Why the rule exists: a pict-section-form marshal reads values through manyfest's getValueByHash, which
 * substitutes the descriptor default for any address the model does not hold yet — and, with no explicit
 * `Default`, that is manyfest's per-DataType default. String gets '', but Number/Integer/Float get 0. So
 * a pristine `{ DataType: 'Number', InputType: 'Picker' }` field — an entity FK, virtually always —
 * marshals in as 0, and the picker would paint a literal "0" as though the user had chosen something.
 * There is no entity #0; it reads as a bug. Treating it as unset is DISPLAY-side only: the stored value
 * is left alone, so 0-means-unset stays a valid pattern for solvers, exports, and the API.
 *
 * A descriptor overrides with `PictForm.EmptyValues` — its own sentinel list, or `[]` to opt out and
 * show the raw 0.
 *
 * pict-section-picker owns the base rule (its form adapter feeds the list to the widget as its
 * `EmptyValues` option, which is what actually suppresses the paint). Headlight extends it for the
 * Materials family, and the broker hands the result to the widget through the adapter's
 * `_resolveEmptyValues` seam, so the control, `__Record` and Read/Print all resolve it here. Requires
 * pict-section-picker >= 1.9.4.
 */
const libPSPResolveEmptyValues = require('pict-section-picker/form').resolveEmptyValues;
const _SPECIALIZED_SELECTOR_MAP = require('./_SpecializedSelectorMap.js');

/**
 * The descriptor's own `PictForm.EmptyValues` wins; else a Materials-family InputType treats 0 as unset
 * whatever its DataType (legacy MES read `pValue == ''`), and the stored literals "null" / "undefined" too
 * (legacy MES skipped them; MaterialsCascadePicker._isClearedPickerValue reads them as a clear); else PSP's
 * per-DataType rule.
 * @param {Record<string, any>} pInput @return {Array<any>}
 */
const resolveEmptyValues = (pInput) =>
{
	const tmpPictForm = (pInput && pInput.PictForm) || {};
	if (!Array.isArray(tmpPictForm.EmptyValues) && _SPECIALIZED_SELECTOR_MAP[tmpPictForm.InputType]?.MaterialsFamily)
	{
		return [ 0, 'null', 'undefined' ];
	}
	return libPSPResolveEmptyValues(pInput);
};

/**
 * True when a single stored value is one of this descriptor's unset sentinels. String-compared, so the
 * numeric 0 and the "0" that comes back off the hidden informary input are the same thing.
 * @param {Record<string, any>} pInput @param {any} pValue @return {boolean}
 */
const isEmptyValue = (pInput, pValue) =>
	resolveEmptyValues(pInput).some((pEmpty) => String(pEmpty) === String(pValue));

module.exports = { resolveEmptyValues, isEmptyValue };
